/**
 * Level manifest — the single source of truth for level ordering.
 *
 * All paths are relative to src/levels/ (without .json extension).
 * Levels are never renamed; only this manifest controls ordering.
 *
 * Buckets: track × difficulty. Every level lives in exactly one bucket
 * and IDs are globally unique, so completion tracking doesn't need to
 * namespace by bucket.
 */

import type { LevelTrack, PuzzleDifficulty } from '../types';

export const LEVELS: Record<LevelTrack, Record<PuzzleDifficulty, string[]>> = {
  progression: {
    casual: [
      // --- Original 10 levels (easier, 4–13 sub-puzzles) ---
      'nature/moon',                   // p=13, 20x20
      'objects/sword',                 // p= 8, 20x10
      'nature/fire',                   // p=10, 20x20
      'objects/flag',                  // p= 7, 20x20
      'objects/potion',                // p= 4, 20x20
      'objects/coffee',                // p=10, 20x20
      'objects/cake',                  // p=13, 25x25
      'objects/crown',                 // p= 9, 20x20
      'objects/tea',                   // p= 9, 20x20
      'objects/diamond-gem',           // p= 9, 25x25
      // --- Extended progression (16–25 sub-puzzles, higher difficulty) ---
      'tools/printer-minimal-sharp',   // p=16, d=64
      'buildings/parking-sharp',       // p=16, d=64
      'media/album',                   // p=16, d=65
      'objects/credit-card',           // p=16, d=65
      'buildings/wallet',              // p=16, d=66
      'media/clapperboard',            // p=16, d=66
      'tools/calculator',              // p=16, d=66
      'buildings/window-frame',        // p=16, d=66
      'buildings/wall',                // p=16, d=67
      'tools/colors-swatch',           // p=16, d=67
      'media/picture-in-picture',      // p=16, d=67
      'objects/money',                 // p=16, d=68
      'transport/card-text',           // p=16, d=68
      'buildings/door-closed',         // p=16, d=68
      'buildings/bed',                 // p=16, d=68
      'media/video',                   // p=16, d=69
      'objects/anchor',                // p=16, d=69
      'nature/thermometer',            // p=16, d=70
      'tools/flatten',                 // p=16, d=70
      'tools/magic-edit',              // p=16, d=70
    ],
    advanced: [
      'nature/moon-advanced',
      'objects/sword-advanced',
      'nature/fire-advanced',
      'objects/flag-advanced',
      'objects/potion-advanced',
      'objects/coffee-advanced',
      'objects/cake-advanced',
      'objects/crown-advanced',
      'objects/tea-advanced',
      'objects/diamond-gem-advanced',
      'tools/printer-minimal-sharp-advanced',
      'buildings/parking-sharp-advanced',
      'media/album-advanced',
      'objects/credit-card-advanced',
      'buildings/wallet-advanced',
      'media/clapperboard-advanced',
      'tools/calculator-advanced',
      'buildings/window-frame-advanced',
      'buildings/wall-advanced',
      'tools/colors-swatch-advanced',
      'media/picture-in-picture-advanced',
      'objects/money-advanced',
      'transport/card-text-advanced',
      'buildings/door-closed-advanced',
      'buildings/bed-advanced',
      'media/video-advanced',
      'objects/anchor-advanced',
      'nature/thermometer-advanced',
      'tools/flatten-advanced',
      'tools/magic-edit-advanced',
    ],
  },
  daily: {
    casual: [
      'community/daily-2026-04-17',
      'community/daily-2026-04-18',
      'community/daily-2026-04-19',
      'community/daily-2026-04-20',
      'community/daily-2026-04-21',
      'community/daily-2026-04-22',
      'community/daily-2026-04-23',
      'community/daily-2026-04-24',
      'community/daily-2026-04-25',
      'community/daily-2026-04-26',
      'community/daily-2026-04-27',
      'community/daily-2026-04-28',
      'community/daily-2026-04-29',
      'community/daily-2026-04-30',
      'community/daily-2026-05-01',
      'community/daily-2026-05-02',
      'community/daily-2026-05-03',
      'community/daily-2026-05-04',
      'community/daily-2026-05-05',
      'community/daily-2026-05-06',
      'community/daily-2026-05-07',
      'community/daily-2026-05-08',
      'community/daily-2026-05-09',
      'community/daily-2026-05-10',
      'community/daily-2026-05-11',
      'community/daily-2026-05-12',
      'community/daily-2026-05-13',
      'community/daily-2026-05-14',
      'community/daily-2026-05-15',
      'community/daily-2026-05-16',
      'community/daily-2026-05-17',
      'community/daily-2026-05-18',
      'community/daily-2026-05-19',
      'community/daily-2026-05-20',
      'community/daily-2026-05-21',
      'community/daily-2026-05-22',
      'community/daily-2026-05-23',
      'community/daily-2026-05-24',
      'community/daily-2026-05-25',
      'community/daily-2026-05-26',
      'community/daily-2026-05-27',
      'community/daily-2026-05-28',
      'community/daily-2026-05-29',
      'community/daily-2026-05-30',
      'community/daily-2026-05-31',
      'community/daily-2026-06-01',
      'community/daily-2026-06-02',
    ],
    advanced: [
      'community/daily-2026-04-17-advanced',
      'community/daily-2026-04-18-advanced',
      'community/daily-2026-04-19-advanced',
      'community/daily-2026-04-20-advanced',
      'community/daily-2026-04-21-advanced',
      'community/daily-2026-04-22-advanced',
      'community/daily-2026-04-23-advanced',
      'community/daily-2026-04-24-advanced',
      'community/daily-2026-04-25-advanced',
      'community/daily-2026-04-26-advanced',
      'community/daily-2026-04-27-advanced',
      'community/daily-2026-04-28-advanced',
      'community/daily-2026-04-29-advanced',
      'community/daily-2026-04-30-advanced',
      'community/daily-2026-05-01-advanced',
      'community/daily-2026-05-02-advanced',
      'community/daily-2026-05-03-advanced',
      'community/daily-2026-05-04-advanced',
      'community/daily-2026-05-05-advanced',
      'community/daily-2026-05-06-advanced',
      'community/daily-2026-05-07-advanced',
      'community/daily-2026-05-08-advanced',
      'community/daily-2026-05-09-advanced',
      'community/daily-2026-05-10-advanced',
      'community/daily-2026-05-11-advanced',
      'community/daily-2026-05-12-advanced',
      'community/daily-2026-05-13-advanced',
      'community/daily-2026-05-14-advanced',
      'community/daily-2026-05-15-advanced',
      'community/daily-2026-05-16-advanced',
      'community/daily-2026-05-17-advanced',
      'community/daily-2026-05-18-advanced',
      'community/daily-2026-05-19-advanced',
      'community/daily-2026-05-20-advanced',
      'community/daily-2026-05-21-advanced',
      'community/daily-2026-05-22-advanced',
      'community/daily-2026-05-23-advanced',
      'community/daily-2026-05-24-advanced',
      'community/daily-2026-05-25-advanced',
      'community/daily-2026-05-26-advanced',
      'community/daily-2026-05-27-advanced',
      'community/daily-2026-05-28-advanced',
      'community/daily-2026-05-29-advanced',
      'community/daily-2026-05-30-advanced',
      'community/daily-2026-05-31-advanced',
      'community/daily-2026-06-01-advanced',
      'community/daily-2026-06-02-advanced',
    ],
  },
};
